import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type * as associations from '../../associations'
import type * as models from '../../models'
import type { Collection } from '../collection'

/**
 * Executes all actions needed to manage associations between entities
 */
export const manageAssociations = store.effects.create(
  <TTypeParams extends models.TypeParams.Constraint>(
    feature: Collection<TTypeParams>['feature'],
    collection: Collection<TTypeParams>,
    manager: associations.Manager<TTypeParams> | undefined,
  ) => {
    if (manager === undefined) {
      return
    }
    let previousTracker:
      | associations.internals.Tracker<
          TTypeParams['entity'] | TTypeParams['createEntity']
        >
      | undefined
    // We need to wait until the collection is ready before we can start
    // accessing plugins
    return collection.ready$.pipe(
      rxjs.exhaustMap(() =>
        collection.plugins.currentEntityPlugin.feature.selectors
          .getLatestEntityAndId()
          .asObservable.pipe(
            rxjs.switchMap(({ entity, id }) => {
              // We only create a tracker if we have an entity to associate with
              const tracker = entity
                ? new manager.trackerConstructor(collection, entity)
                : undefined
              // We create actions to release any selections for associations
              // that are no longer in place
              const releaseActions =
                previousTracker?.associations
                  .filter(
                    // If tracker doesn't exist or doesn't have this
                    // association, we will release it
                    association => !tracker?.hasAssociation(association.id),
                  )
                  .map(association =>
                    association.collection$.pipe(
                      rxjs.map(associatedCollection =>
                        associatedCollection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
                          .from(
                            'manageAssociations (effect) - release previous selection target',
                          )
                          .create({
                            key: association.selectionTarget,
                            value: { release: true },
                          }),
                      ),
                    ),
                  ) ?? []
              previousTracker = tracker
              // We execute all of the below actions in parallel
              return rxjs.merge(
                ...releaseActions,
                // For each association, if there are any...
                ...(tracker?.associations.map(association =>
                  association.collection$.pipe(
                    rxjs.switchMap(associatedCollection => {
                      // Selects the derived selection
                      const selectAssociatedEntitiesAction =
                        associatedCollection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
                          .from(
                            'manageAssociations (effect) - set derived selection',
                          )
                          .create({
                            key: association.selectionTarget,
                            value: { set: association.selectionIds },
                          })
                      return rxjs.concat(
                        rxjs.of(selectAssociatedEntitiesAction),
                        // Listens to changes in associated selections, and applies
                        // them as updates to the current entity
                        rxjs
                          .merge(
                            // We need to wait for the initial selection to
                            // complete, regardless of how it turns out
                            associatedCollection.plugins.selectionPlugin.applied.actions.putInSelectionLookup.success.observe(),
                            associatedCollection.plugins.selectionPlugin.applied.actions.putInSelectionLookup.error.observe(),
                            associatedCollection.plugins.selectionPlugin.applied.actions.putInSelectionLookup.cancellation.observe(),
                          )
                          .pipe(
                            // This ensures we are waiting for the result that
                            // corresponds to the above request
                            rxjs.filter(
                              action =>
                                action.requestId ===
                                selectAssociatedEntitiesAction.requestId,
                            ),
                            // We just need the first result
                            rxjs.take(1),
                            // Then we can switch over to listening to the
                            // selections
                            rxjs.switchMap(() =>
                              associatedCollection.plugins.selectionPlugin.applied.selectors
                                .getFromSelectionLookup(
                                  association.selectionTarget,
                                )
                                .asObservable.pipe(
                                  // We only care about changes in selection. We
                                  // can skip the initial value since that is
                                  // just the selection that we set above
                                  rxjs.skip(1),
                                  rxjs.map(ids =>
                                    // TODO: we should probably only use edits if the
                                    // entity is being edited, and maybe just directly
                                    // update if the entity is not being edited
                                    collection.plugins.editingPlugin.feature.actions.edit
                                      .from(
                                        'manageAssociations (effect) - set derived selection',
                                      )
                                      .create({
                                        edit: editedEntity => {
                                          if (!editedEntity) {
                                            return
                                          }
                                          return association.update(
                                            editedEntity,
                                            ids,
                                          )
                                        },
                                        id,
                                        // NOTE: this entity should already be current
                                        makeCurrent: false,
                                      }),
                                  ),
                                ),
                            ),
                          ),
                      )
                    }),
                  ),
                ) ?? []),
              )
            }),
          ),
      ),
    )
  },
)
